import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AdjustmentBreakdown,
  TierBreakdown,
} from "features/distributions/DistributionTiersBreakdown";
import { getWaterfallBreakdownRetrieveQueryKey } from "gen/endpoints/fund-family-event/fund-family-event";
import {
  useCarriedInterestAdjustmentCreateOrUpdate,
  useCarriedInterestAdjustmentDelete,
} from "gen/endpoints/fund-family-sub-event/fund-family-sub-event";
import { CarriedInterestAdjustmentTypeEnum } from "gen/model/carriedInterestAdjustmentTypeEnum";
import { WaterfallDistributionData } from "gen/model/waterfallDistributionData";
import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { formatMoney } from "shared/utils/MoneyUtils";
import { useBoolean } from "usehooks-ts";

interface WaterfallAdjustmentsModalProps {
  distributionData: WaterfallDistributionData | undefined;
  isOpen: boolean;
  onSuccess(): void;
  onCloseComplete(): void;
  fundId: string;
}

type AdjustmentFormType = {
  [key in CarriedInterestAdjustmentTypeEnum]: string;
};

export const WaterfallAdjustmentsModal: React.FC<
  WaterfallAdjustmentsModalProps
> = ({ distributionData, isOpen, onSuccess, onCloseComplete, fundId }) => {
  const { t } = useTranslation(["distribution"]);
  const { value: modalIsOpen, setFalse: close } = useBoolean(isOpen);

  const form = useForm<AdjustmentFormType>();
  const { fundFamilyId, fundFamilyEventId, fundFamilySubEventId } =
    useRequiredStringParams();

  const fields = form.watch();
  const modifiedField = useMemo(
    () =>
      Object.keys(fields).find(
        (key) => !!Number(fields[key as keyof AdjustmentFormType]),
      ),
    [fields],
  );

  const queryClient = useQueryClient();

  const { mutateAsync: updateCarriedInterests } =
    useCarriedInterestAdjustmentCreateOrUpdate();

  const { mutateAsync: destoryCarriedInterestAdjustments } =
    useCarriedInterestAdjustmentDelete();

  const onSave = useCallback(
    async (data: {
      [key in CarriedInterestAdjustmentTypeEnum]: string;
    }) => {
      // There is only one entry in the object so we can safely destructure it.
      const adjustment_amount = data[modifiedField as keyof AdjustmentFormType];
      if (!modifiedField || !adjustment_amount) {
        return;
      }
      await updateCarriedInterests({
        fundFamilyId,
        fundFamilySubEventId,
        fundEntityId: fundId,
        data: {
          adjustment_type: modifiedField as CarriedInterestAdjustmentTypeEnum,
          adjustment_amount,
        },
      });
    },
    [
      fundFamilyId,
      fundFamilySubEventId,
      fundId,
      updateCarriedInterests,
      modifiedField,
    ],
  );

  const onDelete = useCallback(async () => {
    await destoryCarriedInterestAdjustments({
      fundFamilyId,
      fundFamilySubEventId,
      fundEntityId: fundId,
    });
    await queryClient.invalidateQueries({
      queryKey: getWaterfallBreakdownRetrieveQueryKey(
        fundFamilyId,
        fundFamilyEventId,
        fundFamilySubEventId,
        fundId,
      ),
    });
    close();
  }, [
    fundFamilyId,
    fundFamilyEventId,
    fundFamilySubEventId,
    fundId,
    close,
    destoryCarriedInterestAdjustments,
    queryClient,
  ]);

  return (
    <Modal
      isOpen={modalIsOpen}
      onClose={close}
      onCloseComplete={onCloseComplete}
      size="6xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <Form
          form={form}
          onSubmit={onSave}
          onSuccess={() => {
            onSuccess();
            close();
          }}
        >
          <ModalHeader>
            {t("distribution:adjustments.adjustWaterfall")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody px={0}>
            <AdjustmentsTable
              distributionData={distributionData}
              modifiedField={modifiedField}
            />
          </ModalBody>
          <ModalFooter gap={2} borderTopWidth="1px">
            <Button
              variant="subtle"
              colorScheme="destructive"
              onClick={onDelete}
            >
              {t("distribution:adjustments.removeAdjustments")}
            </Button>
            <Spacer />
            <ButtonOrder
              dismiss={<FormCancelButton onClick={close} />}
              confirm={
                <FormSubmitButton isDisabled={!modifiedField}>
                  {t("distribution:adjustments.saveAdjustments")}
                </FormSubmitButton>
              }
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};

interface AdjustmentsTableProps {
  distributionData: WaterfallDistributionData | undefined;
  modifiedField: string | undefined;
}

const AdjustmentsTable: React.FC<AdjustmentsTableProps> = ({
  distributionData,
  modifiedField,
}) => {
  const { t } = useTranslation(["distribution"]);
  if (!distributionData) {
    return null;
  }
  const { adjustment_distribution_data } = distributionData;
  return (
    <TableBox boxProps={{ sx: { borderRadius: "0px" } }}>
      <TableBoxHeader>
        <TableBoxTitle>
          {t("distribution:adjustments.breakdownOfCalculations")}
        </TableBoxTitle>
      </TableBoxHeader>
      <TableBoxBody>
        <TableContainer height="100%" overflowY="auto">
          <Table
            size="md"
            sx={{
              "& thead th": {
                _notFirst: {
                  textAlign: "right",
                  px: "32px",
                },
              },
              "& tbody td": {
                w: "200px",
                p: "12px",
                borderLeftWidth: "1px",
                borderLeftColor: "gray.200",
                bg: "gray.25",
                textAlign: "right",
              },
              borderCollapse: "collapse",
            }}
          >
            <Thead>
              <Tr>
                <Th>{t("distribution:tiersBreakdown.step")}</Th>
                <Th>{t("distribution:tiersBreakdown.total")}</Th>
                <Th>{t("distribution:tiersBreakdown.participating")}</Th>
                <Th>{t("distribution:tiersBreakdown.carryPartners")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Th>
                  <Box ml="9px">
                    {t("distribution:tiersBreakdown.totalToDistribute")}
                  </Box>
                </Th>
                <Td>
                  {formatMoney(distributionData.total_distribution_amount)}
                </Td>
                <Td>{formatMoney("0")}</Td>
                <Td>{formatMoney("0")}</Td>
              </Tr>
              {distributionData.detailed_distribution_data.map(
                (tier, index) => (
                  <TierBreakdown key={`tier_${index}`} tier={tier} />
                ),
              )}
              <Tr sx={{ borderBottom: "1px solid", borderColor: "gray.200" }}>
                <Th sx={{ py: "24px" }}>
                  <TableBoxTitle>
                    {t("distribution:adjustments.adjustments")}
                  </TableBoxTitle>
                </Th>
              </Tr>
              {adjustment_distribution_data.map((tier, index) => (
                <AdjustmentBreakdown
                  key={`tier_${index}`}
                  tier={tier}
                  isDisabled={
                    !!modifiedField && modifiedField !== tier.adjustment_type
                  }
                />
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </TableBoxBody>
    </TableBox>
  );
};
